// src/prototype/pages/EAROIModelPage.tsx
// ============================================================
// EA ROI MODEL PAGE — ENGINE-DRIVEN
//
// Every figure on this page comes from src/prototype/roi/roiEngine.ts. There are
// no financial literals in this file: the header stats, all four scenario cards,
// the scenario narrative, the assumptions table's impact bars, the Year-1
// financial breakdown, the sensitivity tornado and the CSV export all read the
// same computed model.
//
// WHAT THIS REPLACED. Four parallel hardcoded arrays (a generic scenario set, a
// Care Coordination set, two sensitivity sets, two waterfalls), a "2 Modified"
// badge that was a constant, and a Custom card whose numbers could not move
// because the assumptions the presenter edited were not wired to anything. The
// only feedback an edit produced was an orange tint on its own row.
//
// The presenter now edits the Custom column and the Custom card recomputes —
// value, payback, net value, return and confidence — because those inputs ARE
// the model's inputs.
//
// Route: /opportunity/:id/roi-model  (also embedded in the Use-Case Hub ROI tab)
// ============================================================

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useEntitlements } from '../hooks/useEntitlements';
import {
  ChevronDown,
  ChevronRight,
  Download,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  HelpCircle,
  Activity,
  CheckCircle2,
  Sparkles,
  ArrowUpRight,
  Info,
  Target,
  DollarSign,
  Settings,
  Users,
  Database,
  Lightbulb,
  Calculator,
} from 'lucide-react';
import { SectionHeaderBand } from '../components/hub/SectionHeaderBand';
import { EAJourneyBarFrame } from '../components/EAJourneyBarFrame';
import { RelatedViewsToolbar, RestoreState } from '../components/RelatedViewsToolbar';
import { getOpportunityById } from '../data/mockOpportunities';
import {
  computeRoiModel,
  formatMonths,
  formatUsd,
  formatUsdRange,
  isModified,
  isScenarioVaried,
  resolveDrivers,
  type ConfidenceLevel,
  type RoiDriver,
  type RoiScenarioOutputs,
  type ScenarioId,
} from '../roi/roiEngine';
import {
  buildDisclosure,
  CANON_CONFIDENCE_BASIS,
  formatRoiPctFor,
  ROI_CONFIG,
  ROI_DISPLAY,
  ROI_PCT_CAPTION,
  ROI_PCT_LABEL,
} from '../roi/roiCanon';
import { getRoiDrivers } from '../roi/useCaseRoi';

// ============================================================================
// SCENARIO PRESENTATION
//
// Names, sublabels, descriptions and risks. Deliberately carries NO figures —
// every number in the detail panel is derived from the engine below, so a
// changed assumption cannot leave stale prose behind it.
// ============================================================================

const SCENARIO_META: Record<
  ScenarioId,
  { name: string; sublabel: string; description: string; risks: string[] }
> = {
  conservative: {
    name: 'Conservative',
    sublabel: 'Slower adoption',
    description:
      'Lower-bound intervention performance with a slower care-management ramp.',
    risks: [
      'Care-management staffing ramp dependencies',
      'Readmission attribution baseline completeness',
    ],
  },
  base: {
    name: 'Base Case',
    sublabel: 'Most likely',
    description:
      'Most likely outcome at benchmarked intervention performance and planned adoption.',
    risks: ['Care-management staffing ramp dependencies', 'Payer contract timing'],
  },
  aggressive: {
    name: 'Aggressive',
    sublabel: 'Best-case conditions',
    description:
      'Upper-bound intervention performance with accelerated adoption across care settings.',
    risks: [
      'Aggressive ramp may strain care-management capacity',
      'Sustaining performance across all care settings',
    ],
  },
  custom: {
    name: 'Custom',
    sublabel: 'User Modified',
    description: 'Your scenario, computed from the values in the Custom column below.',
    risks: ['Custom assumptions require validation with Finance'],
  },
};

const SCENARIO_ORDER: ScenarioId[] = ['conservative', 'base', 'aggressive', 'custom'];

const GROUP_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'Value Drivers': DollarSign,
  Adoption: Users,
  'Investment Schedule': Target,
};

// ============================================================================
// SMALL PRESENTATION HELPERS
// ============================================================================

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}>
    {children}
  </span>
);

const confidenceChip = (level: ConfidenceLevel): string =>
  level === 'High'
    ? 'bg-[#18365E]/10 text-[#18365E] border-[#18365E]/30'
    : level === 'Medium'
      ? 'bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30'
      : 'bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/30';

/**
 * Year-1 financial breakdown. Bar widths AND labels both derive from `value` —
 * the previous version carried a hand-written `displayValue` string alongside the
 * number, which is how the CSV export came to disagree with the screen.
 */
const WaterfallChart: React.FC<{ scenario: RoiScenarioOutputs }> = ({ scenario }) => {
  const rows = [
    { name: 'Baseline', value: 0, color: '#e4e4e7' },
    { name: 'Benefits', value: scenario.year1RealizedValue, color: '#18365E', sign: '+' },
    { name: 'Costs', value: scenario.year1Cost, color: '#ef4444', sign: '-' },
    { name: 'Net Value', value: scenario.year1NetValue, color: '#FF6E4C' },
  ];
  const maxValue = Math.max(...rows.map((r) => Math.abs(r.value))) * 1.1 || 1;

  return (
    <div className="space-y-3" data-testid="roi-waterfall">
      {rows.map((row) => (
        <div key={row.name} className="flex items-center gap-3">
          <div className="w-24 text-[10px] font-medium text-[#52525b] text-right">{row.name}</div>
          <div className="flex-1 h-8 bg-[#f5f5f5] rounded relative overflow-hidden">
            <div
              className="h-full rounded transition-all"
              style={{
                width: `${(Math.abs(row.value) / maxValue) * 100}%`,
                backgroundColor: row.color,
              }}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#27272a]">
              {row.sign ?? ''}
              {formatUsd(row.value)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// `embedded` renders the ROI model INSIDE the Use-Case Hub's ROI tab: its own journey
// bar, related-views toolbar, outer page background, and Pro-redirect are suppressed (the
// hub owns that chrome and gating). All ROI functionality is otherwise unchanged.
const EAROIModelPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  // Support both :id and :opportunityId route parameter names
  const params = useParams<{ id?: string; opportunityId?: string }>();
  const useCaseId = params.id || params.opportunityId || '';
  const location = useLocation();

  // The full ROI model is a Pro capability (even for the top-3 use cases).
  const { can } = useEntitlements();

  // Extract restore state from location
  const restoreState: RestoreState | undefined = location.state?.restore;
  const fromStep3 = location.state?.from === 'activate';

  const canonicalOpp = useCaseId ? getOpportunityById(useCaseId) : undefined;
  const title = canonicalOpp?.title ?? 'Use Case Analysis';
  const category = canonicalOpp?.category ?? 'Analytics Initiative';

  // ── STATE ────────────────────────────────────────────────────────────────
  // The driver set IS the model's input vector. Editing the Custom column edits
  // this, and every figure on the page recomputes from it.
  const [drivers, setDrivers] = useState<RoiDriver[]>(() => getRoiDrivers(useCaseId));
  const [activeScenario, setActiveScenario] = useState<ScenarioId>('base');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({
    'Investment Schedule': true,
  });
  const [showDisclosure, setShowDisclosure] = useState(false);

  // A different initiative is a different model — reset rather than carry the
  // previous initiative's custom edits across.
  useEffect(() => {
    setDrivers(getRoiDrivers(useCaseId));
    setActiveScenario('base');
  }, [useCaseId]);

  // Scroll to top on mount (the hub owns scroll when embedded).
  useEffect(() => {
    if (!embedded) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [embedded]);

  // Pro gate: freemium is routed to the conversion surface. When embedded, the hub owns
  // gating (it renders an upgrade panel instead of mounting this page for freemium).
  useEffect(() => {
    if (!embedded && !can.viewRoiModel) navigate('/upgrade', { replace: true });
  }, [embedded, can.viewRoiModel, navigate]);

  // ── THE MODEL ────────────────────────────────────────────────────────────
  const model = useMemo(() => computeRoiModel(drivers, ROI_CONFIG), [drivers]);
  const selected = model.scenarios[activeScenario];
  const baseScenario = model.scenarios.base;
  const disclosure = useMemo(() => buildDisclosure(model), [model]);

  // Impact bars in the assumptions table are the driver's sensitivity span,
  // normalised against the strongest driver — a real ranking, not a hand score.
  const impactByDriver = useMemo(() => {
    const maxSpan = Math.max(...model.sensitivity.map((r) => r.span), 1);
    return new Map(
      model.sensitivity.map((r) => [r.driverId, Math.round((r.span / maxSpan) * 100)]),
    );
  }, [model.sensitivity]);

  const groups = useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, RoiDriver[]>();
    for (const driver of drivers) {
      if (!byGroup.has(driver.group)) {
        byGroup.set(driver.group, []);
        order.push(driver.group);
      }
      byGroup.get(driver.group)!.push(driver);
    }
    return order.map((name) => ({ name, drivers: byGroup.get(name)! }));
  }, [drivers]);

  // ── SCENARIO NARRATIVE, DERIVED ──────────────────────────────────────────
  // Value drivers and key assumptions are generated from the resolved chain, so
  // the bullets restate the arithmetic rather than a snapshot of it.
  const narrative = useMemo(() => {
    const resolved = resolveDrivers(drivers, activeScenario);
    const scenarioValue = (driver: RoiDriver) =>
      activeScenario === 'custom' ? driver.custom : driver[activeScenario];

    const chainDriver =
      drivers.find((d) => d.role === 'valueMultiplier' && isScenarioVaried(d)) ??
      drivers.find((d) => d.role === 'valueMultiplier');

    const valueDrivers: string[] = [];
    if (resolved.multiplierValue > 0 && chainDriver) {
      valueDrivers.push(
        `${chainDriver.name.replace(/\s*\([^)]*\)\s*$/, '')} at ${scenarioValue(chainDriver)}${
          chainDriver.unit
        }: ${formatUsd(resolved.multiplierValue)} annually`,
      );
    }
    for (const driver of drivers.filter((d) => d.role === 'valueAddend')) {
      valueDrivers.push(
        `${driver.name.replace(/\s*\([^)]*\)\s*$/, '')}: ${formatUsd(
          scenarioValue(driver) * driver.scale,
        )} annually`,
      );
    }

    const keyAssumptions = [
      ...drivers
        .filter((d) => isScenarioVaried(d) && d.role !== 'ramp')
        .map((d) => `${d.name.replace(/\s*\([^)]*\)\s*$/, '')}: ${scenarioValue(d)}${d.unit}`),
      `Adoption reaches full run-rate in ${selected.rampMonths} months — Year 1 realizes ${Math.round(
        selected.year1RampFraction * 100,
      )}% of it`,
      `Investment held at ${formatUsd(selected.implementationCost)} implementation + ${formatUsd(
        selected.operatingCostPerYear,
      )}/yr operating`,
    ];

    return { valueDrivers, keyAssumptions, risks: SCENARIO_META[activeScenario].risks };
  }, [drivers, activeScenario, selected]);

  // The insight panel names the drivers the tornado actually ranked first.
  const insight = useMemo(() => {
    const [first, second] = model.sensitivity;
    if (!first) return null;
    const strip = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, '');
    return `${strip(first.name)} (#1)${second ? ` and ${strip(second.name)} (#2)` : ''} ${
      second ? 'are' : 'is'
    } the highest-impact assumption${second ? 's' : ''} in this model. Across its tested band of ${
      first.lowValue
    }–${first.highValue}, net 3-year value moves by ${formatUsd(first.span)}. Validate ${
      second ? 'these' : 'this'
    } before funding.`;
  }, [model.sensitivity]);

  // Navigation labels
  const backLabel = fromStep3 ? 'Back to Get Approval & Execute' : 'Back to Evaluate & Shortlist';
  const backPath = fromStep3 ? '/activate' : '/opportunities';

  // ── EDITING ──────────────────────────────────────────────────────────────
  const handleDriverEdit = (driverId: string, nextValue: number) => {
    setDrivers((previous) =>
      previous.map((driver) =>
        driver.id === driverId ? { ...driver, custom: nextValue } : driver,
      ),
    );
    // An edit is only visible on the Custom card, so select it.
    setActiveScenario('custom');
  };

  const handleResetDriver = (driverId: string) => {
    setDrivers((previous) =>
      previous.map((driver) =>
        driver.id === driverId ? { ...driver, custom: driver.base } : driver,
      ),
    );
  };

  const handleResetAll = () => {
    setDrivers((previous) => previous.map((driver) => ({ ...driver, custom: driver.base })));
  };

  const toggleGroup = (name: string) =>
    setCollapsedGroups((previous) => ({ ...previous, [name]: !previous[name] }));

  // ── CSV EXPORT ───────────────────────────────────────────────────────────
  // Reads the same model as the screen. The previous version wrote a hardcoded
  // waterfall, so exporting Care Coordination produced $3.2M/$980K/$2.22M while
  // the page showed entirely different figures.
  const handleExportCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `roi-model-${title.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.csv`;
    const rows: string[] = [];

    rows.push('ROI Model Export');
    rows.push(`Use Case,${title}`);
    rows.push(`Category,${category}`);
    rows.push(`Export Date,${new Date().toLocaleDateString()}`);
    rows.push('');

    rows.push('MODEL DEFINITIONS');
    rows.push('Term,Definition');
    disclosure.forEach((line) => rows.push(`"${line.term}","${line.definition}"`));
    rows.push('');

    rows.push('SCENARIO SUMMARY');
    rows.push(
      'Scenario,Annual Value Low,Annual Value High,Year-1 Realized Value,Year-1 Net Value,Payback (months),Net 3-Year Value,ROI Year-1 (%),ROI 3-Year (%),Return Multiple,Confidence (%),Confidence',
    );
    SCENARIO_ORDER.forEach((id) => {
      const out = model.scenarios[id];
      rows.push(
        [
          SCENARIO_META[id].name,
          Math.round(out.annualValueLow),
          Math.round(out.annualValueHigh),
          Math.round(out.year1RealizedValue),
          Math.round(out.year1NetValue),
          out.paybackMonths ?? 'not recovered',
          Math.round(out.netValue3Yr),
          Math.round(out.year1RoiPct),
          Math.round(out.roi3YrPct),
          out.returnMultiple.toFixed(2),
          out.confidencePct,
          out.confidenceLabel,
        ].join(','),
      );
    });
    rows.push('');

    rows.push('MODEL INPUTS');
    rows.push(
      'Group,Input,Conservative,Base,Aggressive,Custom,Unit,Scenario Lever,Band Low,Band High,Confidence,Source,Source Type,Impact',
    );
    groups.forEach((group) => {
      group.drivers.forEach((driver) => {
        rows.push(
          [
            group.name,
            `"${driver.name}"`,
            driver.conservative,
            driver.base,
            driver.aggressive,
            driver.custom,
            driver.unit,
            isScenarioVaried(driver) ? 'yes' : 'no (canon)',
            driver.bandLow ?? driver.conservative,
            driver.bandHigh ?? driver.aggressive,
            driver.confidence,
            `"${driver.source}"`,
            driver.sourceType,
            impactByDriver.get(driver.id) ?? 0,
          ].join(','),
        );
      });
    });
    rows.push('');

    rows.push('SENSITIVITY (each input across its own band, vs net 3-year value)');
    rows.push('Rank,Input,Base Value,Band Low,Band High,Downside Delta,Upside Delta,Span,Confidence');
    model.sensitivity.forEach((row) => {
      rows.push(
        [
          row.rank,
          `"${row.name}"`,
          row.baseLabel,
          row.lowValue,
          row.highValue,
          Math.round(row.downsideDelta),
          Math.round(row.upsideDelta),
          Math.round(row.span),
          row.confidence,
        ].join(','),
      );
    });
    rows.push('');

    rows.push(`FINANCIAL BREAKDOWN (Year 1 — ${SCENARIO_META[activeScenario].name})`);
    rows.push('Component,Value');
    rows.push('Baseline,0');
    rows.push(`Benefits,${Math.round(selected.year1RealizedValue)}`);
    rows.push(`Costs,${-Math.round(selected.year1Cost)}`);
    rows.push(`Net Value,${Math.round(selected.year1NetValue)}`);

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'External Benchmark':
        return <Database className="w-3.5 h-3.5 text-[#3b82f6]" />;
      case 'Historical':
        return <TrendingUp className="w-3.5 h-3.5 text-[#18365E]" />;
      case 'Estimate':
        return <Lightbulb className="w-3.5 h-3.5 text-[#f59e0b]" />;
      default:
        return <Info className="w-3.5 h-3.5 text-[#71717a]" />;
    }
  };

  // HEADER STATS — order and lead weight both come from ROI_DISPLAY.headerOrder,
  // so the visual hierarchy is the canon decision rather than a layout accident.
  // Payback and net 3-year value lead; ROI% is secondary and carries its formula.
  const STAT_BY_KEY: Record<
    (typeof ROI_DISPLAY.headerOrder)[number],
    { label: string; value: string; caption?: string; badge?: ConfidenceLevel }
  > = {
    payback: { label: 'Payback', value: formatMonths(selected.paybackMonths) },
    netValue: { label: 'Net 3-Year Value', value: formatUsd(selected.netValue3Yr) },
    roiPct: {
      label: ROI_PCT_LABEL,
      value: formatRoiPctFor(selected),
      caption: ROI_PCT_CAPTION,
    },
    confidence: {
      label: 'Confidence',
      value: `${selected.confidencePct}%`,
      badge: selected.confidenceLabel,
    },
  };
  const headerStats = ROI_DISPLAY.headerOrder.map((key, index) => ({
    key,
    ...STAT_BY_KEY[key],
    lead: index < ROI_DISPLAY.leadCount,
  }));

  const modifiedCount = model.scenarios.custom.modifiedCount;
  const maxSensitivityMagnitude = Math.max(
    ...model.sensitivity.map((r) => Math.max(Math.abs(r.downsideDelta), r.upsideDelta)),
    1,
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#F5F7FA]'}>
      {!embedded && <EAJourneyBarFrame sticky />}

      <main className={embedded ? '' : 'px-6 py-4'}>
        {!embedded && (
          <RelatedViewsToolbar
            useCaseId={useCaseId}
            currentView="roi"
            restoreState={restoreState}
            backLabel={backLabel}
            backPath={backPath}
          />
        )}

        {/* Header. Embedded in the hub it is the same colored band as the other hub cards. */}
        {embedded ? (
          <div className="rounded-t-xl border border-[#e5e7eb] border-b-0 overflow-hidden bg-white">
            <SectionHeaderBand
              icon={<TrendingUp className="w-3.5 h-3.5" />}
              title="ROI Model"
              subtitle={
                <>
                  Showing the{' '}
                  <span className="font-semibold text-[#18365E]">{SCENARIO_META[activeScenario].name}</span>{' '}
                  scenario. Annual value {formatUsdRange(selected.annualValueLow, selected.annualValueHigh)} ·{' '}
                  {formatUsd(selected.implementationCost)} implementation + {formatUsd(selected.operatingCostPerYear)}/yr
                  operating · {formatUsd(selected.totalCost3Yr)} total 3-year cost.
                </>
              }
              right={
                <div className="flex items-start gap-5" data-testid="roi-header-stats">
                  {headerStats
                    // The hub header already shows Payback and Net 3-Year Value.
                    .filter((stat) => stat.key === 'roiPct' || stat.key === 'confidence')
                    .map((stat) => (
                      <div
                        key={stat.key}
                        data-testid={`roi-header-stat-${stat.key}`}
                        data-lead="false"
                        className="text-right"
                      >
                        <p className="text-[9px] font-semibold uppercase tracking-wide text-[#717182]">{stat.label}</p>
                        <p className="text-sm font-bold text-[#18365E] leading-tight">
                          {stat.value}
                          {stat.badge && (
                            <span className={`ml-1.5 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded border ${confidenceChip(stat.badge)}`}>
                              {stat.badge}
                            </span>
                          )}
                        </p>
                        {/* Each figure keeps the arithmetic or basis behind it. */}
                        <p className="text-[9px] text-[#717182] leading-snug max-w-[170px] ml-auto mt-0.5">
                          {stat.key === 'confidence' ? CANON_CONFIDENCE_BASIS : stat.caption}
                        </p>
                      </div>
                    ))}
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#d4d4d8] rounded-lg text-xs font-medium text-[#18365E] hover:bg-[#fafafa] hover:border-[#18365E]/40 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export CSV
                  </button>
                </div>
              }
            />
          </div>
        ) : (
        <div className="bg-white border-b border-[#e5e7eb] rounded-t-xl">
          <div className="px-6 py-4">
            <div className={`flex items-center ${embedded ? 'justify-end' : 'justify-between'}`}>
              {!embedded && (
              <div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-[#18365E] text-white px-2 py-1">ROI Model</Badge>
                  <h1 className="text-xl font-semibold text-[#18365E]">{title}</h1>
                </div>
                <p className="text-sm text-[#717182] mt-1">
                  Validate assumptions, model scenarios, run sensitivity analysis, and finalize the
                  ROI story.
                </p>
              </div>
              )}
              <div className="flex items-center gap-6" data-testid="roi-header-stats">
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-[#d4d4d8] rounded-lg text-sm font-medium text-[#18365E] hover:bg-[#fafafa] hover:border-[#18365E]/40 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                {headerStats
                  // Embedded in the hub: the hub header already shows Payback and Net 3-Year Value.
                  .filter((stat) => !embedded || stat.key === 'roiPct' || stat.key === 'confidence')
                  .map((stat) => (
                  <div
                    key={stat.key}
                    data-testid={`roi-header-stat-${stat.key}`}
                    data-lead={stat.lead ? 'true' : 'false'}
                    className={`text-right ${stat.lead ? '' : 'pl-5 border-l border-[#e5e7eb]'}`}
                  >
                    <p
                      className={`uppercase ${
                        stat.lead
                          ? 'text-xs font-semibold text-[#52525b]'
                          : 'text-[10px] font-medium text-[#a1a1aa]'
                      }`}
                    >
                      {stat.label}
                    </p>
                    <p
                      className={
                        stat.lead
                          ? 'text-3xl font-bold text-[#18365E] leading-tight'
                          : 'text-lg font-semibold text-[#52525b] leading-tight'
                      }
                    >
                      {stat.value}
                      {stat.badge && (
                        <span
                          className={`ml-1.5 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded border ${confidenceChip(
                            stat.badge,
                          )}`}
                        >
                          {stat.badge}
                        </span>
                      )}
                    </p>
                    {/* The ROI% never travels without the arithmetic behind it. */}
                    {stat.caption && (
                      <p className="text-[8px] text-[#a1a1aa] leading-snug max-w-[120px] ml-auto mt-0.5">
                        {stat.caption}
                      </p>
                    )}
                    {/* Embedded only: the confidence figure never travels without its basis. */}
                    {embedded && stat.key === 'confidence' && (
                      <p className="text-[8px] text-[#a1a1aa] leading-snug max-w-[200px] ml-auto mt-0.5">
                        {CANON_CONFIDENCE_BASIS}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-[#717182] mt-2">
              Showing the{' '}
              <span className="font-semibold text-[#18365E]">
                {SCENARIO_META[activeScenario].name}
              </span>{' '}
              scenario. Annual value {formatUsdRange(selected.annualValueLow, selected.annualValueHigh)} ·
              Year-1 realized {formatUsd(selected.year1RealizedValue)} ·{' '}
              {formatUsd(selected.implementationCost)} implementation +{' '}
              {formatUsd(selected.operatingCostPerYear)}/yr operating ·{' '}
              {formatUsd(selected.totalCost3Yr)} total 3-year cost.
            </p>
          </div>
        </div>
        )}

        {/* Scenario Performance Overview */}
        <div className="bg-white rounded-b-xl border-x border-b border-[#e5e7eb] mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <h3 className="text-xs text-[#52525b] uppercase tracking-wider font-bold">
                  Scenario Performance Overview
                </h3>
                <HelpCircle className="w-3.5 h-3.5 text-[#71717a]" />
              </div>
              <button
                onClick={() => setShowDisclosure((open) => !open)}
                className="flex items-center gap-1.5 text-[10px] font-semibold text-[#18365E] hover:text-[#FF6E4C] transition-colors"
              >
                <Calculator className="w-3.5 h-3.5" />
                How these are computed
                {showDisclosure ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            </div>

            {/* "How these are computed" — one line per definition, built from the
                engine config so it cannot drift from the arithmetic. */}
            {showDisclosure && (
              <div className="mb-4 rounded-lg border border-[#18365E]/20 bg-[#18365E]/[.03] p-4" data-testid="roi-disclosure">
                <dl className="space-y-1.5">
                  {disclosure.map((line) => (
                    <div key={line.term} className="flex gap-2 text-[10px] leading-relaxed">
                      <dt className="font-bold text-[#18365E] shrink-0 w-[104px]">{line.term}</dt>
                      <dd className="text-[#27272a] flex-1">{line.definition}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-[9px] text-[#717182] mt-3 pt-2 border-t border-[#18365E]/10">
                  Every figure on this page is computed from the inputs below by one shared model.
                  Export CSV includes these definitions and all model inputs.
                </p>
              </div>
            )}

            {/* Scenario Cards */}
            <div className="grid grid-cols-4 gap-3 mb-1.5">
              {SCENARIO_ORDER.map((id) => {
                const out = model.scenarios[id];
                const meta = SCENARIO_META[id];
                const isSelected = id === activeScenario;
                const netDelta = out.netValue3Yr - baseScenario.netValue3Yr;

                return (
                  <button
                    key={id}
                    data-testid={`roi-scenario-${id}`}
                    onClick={() => setActiveScenario(id)}
                    className={`p-3 rounded-lg transition-all duration-200 text-left relative ${
                      isSelected
                        ? 'border-[3px] border-[#18365E] bg-[#18365E]/5 shadow-lg'
                        : 'border-2 border-[#d4d4d8] bg-white hover:border-[#18365E]/40 hover:shadow-md'
                    }`}
                  >
                    {isSelected && (
                      <Badge className="absolute top-2 right-2 text-[8px] px-1.5 py-0 bg-[#18365E] text-white">
                        Active
                      </Badge>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-xs font-bold text-[#18365E] block mb-0.5">
                          {meta.name}
                        </span>
                        <span
                          className={`text-[9px] font-medium ${
                            id === 'custom' ? 'text-[#FF6E4C]' : 'text-[#71717a]'
                          }`}
                        >
                          {meta.sublabel}
                        </span>
                      </div>
                      {/* Counts inputs whose Custom value differs from Base. Zero
                          until the presenter actually changes something. */}
                      {id === 'custom' && out.modifiedCount > 0 && (
                        <Badge className="text-[9px] px-1.5 py-0 bg-[#FF6E4C] text-white">
                          {out.modifiedCount} Modified
                        </Badge>
                      )}
                    </div>
                    {/* LEAD PAIR — payback and net 3-year value, the two figures a
                        CFO gates on, at display weight. */}
                    <div className="grid grid-cols-2 gap-2 mb-2 pb-2 border-b border-[#e4e4e7]">
                      <div>
                        <p className="text-[8px] text-[#71717a] font-bold uppercase tracking-wide">
                          Payback
                        </p>
                        <p className="text-xl font-bold text-[#18365E] leading-none mt-0.5">
                          {out.paybackMonths === null ? '—' : out.paybackMonths}
                          {out.paybackMonths !== null && (
                            <span className="text-[10px] font-semibold text-[#52525b] ml-0.5">mo</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-[8px] text-[#71717a] font-bold uppercase tracking-wide">
                          Net 3-Yr Value
                        </p>
                        <p className="text-xl font-bold text-[#18365E] leading-none mt-0.5 flex items-baseline gap-1">
                          {formatUsd(out.netValue3Yr)}
                          {id !== 'base' && Math.abs(netDelta) >= 50_000 && (
                            <span
                              className={netDelta > 0 ? 'text-[#18365E]' : 'text-[#ef4444]'}
                              title={`${netDelta > 0 ? '+' : '-'}${formatUsd(
                                Math.abs(netDelta),
                              )} vs base`}
                            >
                              {netDelta > 0 ? (
                                <TrendingUp className="w-2.5 h-2.5" />
                              ) : (
                                <TrendingDown className="w-2.5 h-2.5" />
                              )}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {/* SECONDARY — context and the labelled ROI%. */}
                    <div className="space-y-1 mb-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-[#a1a1aa] font-medium">Annual Value</span>
                        <span className="text-[10px] font-semibold text-[#52525b]">
                          {formatUsdRange(out.annualValueLow, out.annualValueHigh)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-[#a1a1aa] font-medium">
                          {ROI_PCT_LABEL}
                        </span>
                        <span className="text-[10px] font-semibold text-[#52525b]">
                          {formatRoiPctFor(out)}
                        </span>
                      </div>
                    </div>
                    <Badge className={`text-[9px] px-1 py-0 border ${confidenceChip(out.confidenceLabel)}`}>
                      {out.confidenceLabel} · {out.confidencePct}%
                    </Badge>
                  </button>
                );
              })}
            </div>

            {/* The ROI% on every card above is defined here once, rather than
                repeated four times in 8px type. */}
            <p className="text-[9px] text-[#a1a1aa] mb-4">
              <span className="font-semibold text-[#71717a]">{ROI_PCT_LABEL}</span> ={' '}
              {ROI_PCT_CAPTION}. Payback and net 3-year value lead because they are what a funding
              gate turns on; the ratio is shown for completeness. Full definitions under{' '}
              <span className="font-semibold text-[#71717a]">How these are computed</span>.
            </p>

            {/* Scenario Detail Panel */}
            <div className="bg-gradient-to-br from-white to-[#fafafa] border border-[#d4d4d8] rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-bold text-[#18365E] flex items-center gap-1.5">
                    <Activity className="w-4 h-4" />
                    Scenario: {SCENARIO_META[activeScenario].name}
                  </h4>
                  <p className="text-[10px] text-[#52525b] font-medium mt-0.5">
                    {SCENARIO_META[activeScenario].description}
                  </p>
                </div>
                <Badge
                  className={`${
                    selected.confidenceLabel === 'High'
                      ? 'bg-[#18365E] text-white'
                      : selected.confidenceLabel === 'Medium'
                        ? 'bg-[#f59e0b] text-white'
                        : 'bg-[#ef4444] text-white'
                  } px-2 py-0.5 text-[10px]`}
                >
                  {selected.confidencePct}% Confidence
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-[10px]">
                <div>
                  <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider font-bold flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#18365E]" />
                    Value Drivers
                  </h5>
                  <ul className="space-y-1 mt-1.5">
                    {narrative.valueDrivers.map((driver, index) => (
                      <li key={index} className="flex items-start gap-1.5">
                        <span className="text-[#18365E] text-xs flex-shrink-0 mt-0.5">✓</span>
                        <span className="text-[#27272a] font-medium">{driver}</span>
                      </li>
                    ))}
                    <li className="flex items-start gap-1.5 pt-1 border-t border-[#e4e4e7] mt-1.5">
                      <span className="text-[#FF6E4C] text-xs flex-shrink-0 mt-0.5">=</span>
                      <span className="text-[#18365E] font-bold">
                        {formatUsd(selected.annualValueMid)} annual midpoint
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="border-l border-[#e4e4e7] pl-4">
                  <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider font-bold flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]">
                    <Target className="w-3.5 h-3.5 text-[#18365E]" />
                    Key Assumptions
                  </h5>
                  <ul className="space-y-1 mt-1.5">
                    {narrative.keyAssumptions.map((assumption, index) => (
                      <li key={index} className="flex items-start gap-1.5">
                        <span className="text-[#3b82f6] text-xs flex-shrink-0 mt-0.5">•</span>
                        <span className="text-[#27272a] font-medium">{assumption}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-l border-[#e4e4e7] pl-4">
                  <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider font-bold flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]">
                    <AlertCircle className="w-3.5 h-3.5 text-[#f59e0b]" />
                    Risks
                  </h5>
                  <ul className="space-y-1 mt-1.5">
                    {narrative.risks.map((risk, index) => (
                      <li key={index} className="flex items-start gap-1.5">
                        <AlertCircle className="w-2.5 h-2.5 text-[#f59e0b] flex-shrink-0 mt-1" />
                        <span className="text-[#27272a] font-medium">{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main 2-Column Layout */}
        <div className="grid grid-cols-3 gap-6">
          {/* LEFT: Assumptions Lab + Waterfall */}
          <div className="col-span-2 space-y-6">
            {/* Assumptions Laboratory */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg">
              <div className="px-4 py-3 border-b border-[#e4e4e7] bg-[#fafafa]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs font-bold text-[#18365E]">Assumptions Laboratory</h3>
                    <HelpCircle className="w-3.5 h-3.5 text-[#71717a]" />
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-[9px] text-[#52525b] font-medium">
                      Edit the Custom column — the Custom scenario recomputes
                    </p>
                    {modifiedCount > 0 && (
                      <button
                        onClick={handleResetAll}
                        className="flex items-center gap-1 text-[9px] font-semibold text-[#71717a] hover:text-[#FF6E4C] transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Reset all ({modifiedCount})
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                    <tr>
                      <th className="px-3 py-2 text-left text-[9px] text-[#52525b] uppercase tracking-wider font-bold">
                        Input
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">
                        Cons.
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">
                        Base
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">
                        Agg.
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-20">
                        Custom
                      </th>
                      <th className="px-2 py-2 text-center text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">
                        Conf.
                      </th>
                      <th className="px-2 py-2 text-left text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-24">
                        Source
                      </th>
                      <th className="px-2 py-2 text-center text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-16">
                        Impact
                      </th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => {
                      const GroupIcon = GROUP_ICONS[group.name] || Settings;
                      const expanded = !collapsedGroups[group.name];
                      return (
                        <React.Fragment key={group.name}>
                          <tr
                            className="bg-[#18365E]/5 cursor-pointer hover:bg-[#18365E]/10 transition-colors"
                            onClick={() => toggleGroup(group.name)}
                          >
                            <td colSpan={9} className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <GroupIcon className="w-4 h-4 text-[#18365E]" />
                                {expanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-[#18365E]" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-[#18365E]" />
                                )}
                                <span className="text-[10px] font-bold text-[#18365E]">
                                  {group.name}
                                </span>
                                <span className="text-[9px] text-[#71717a] font-medium">
                                  ({group.drivers.length})
                                </span>
                              </div>
                            </td>
                          </tr>
                          {expanded &&
                            group.drivers.map((driver, rowIndex) => {
                              // Same predicate the badge counts with — see isModified().
                              const changed = isModified(driver);
                              const varied = isScenarioVaried(driver);
                              const impact = impactByDriver.get(driver.id) ?? 0;
                              return (
                                <tr
                                  key={driver.id}
                                  className={`border-b border-[#e4e4e7] hover:bg-[#18365E]/5 transition-colors ${
                                    changed
                                      ? 'bg-[#FF6E4C]/5'
                                      : rowIndex % 2 === 0
                                        ? 'bg-[#fafafa]/50'
                                        : 'bg-white'
                                  }`}
                                >
                                  <td className="px-3 py-2.5">
                                    <div className="flex items-center gap-1.5">
                                      <Info className="w-3.5 h-3.5 text-[#71717a] shrink-0" />
                                      <span className="text-[#27272a] font-medium">
                                        {driver.name}
                                      </span>
                                      {/* An observed or approved input reads the same in all
                                          three scenarios on purpose. Say so, so identical
                                          columns don't look like missing data. */}
                                      {!varied && (
                                        <span
                                          className="text-[8px] font-semibold text-[#71717a] bg-[#f4f4f5] border border-[#e4e4e7] rounded px-1 py-0.5 shrink-0"
                                          title={`Canon in every scenario. Tested across ${driver.bandLow ?? driver.conservative}–${driver.bandHigh ?? driver.aggressive}${driver.unit} in the sensitivity analysis.`}
                                        >
                                          canon
                                        </span>
                                      )}
                                      {changed && (
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#FF6E4C] shrink-0" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-2 py-2.5 text-right text-[#71717a] font-medium">
                                    {driver.conservative}
                                    {driver.unit}
                                  </td>
                                  <td className="px-2 py-2.5 text-right text-[#18365E] font-semibold">
                                    {driver.base}
                                    {driver.unit}
                                  </td>
                                  <td className="px-2 py-2.5 text-right text-[#71717a] font-medium">
                                    {driver.aggressive}
                                    {driver.unit}
                                  </td>
                                  <td className="px-2 py-2.5">
                                    <input
                                      type="number"
                                      step="any"
                                      value={driver.custom}
                                      aria-label={`${driver.name} custom value`}
                                      onChange={(event) =>
                                        handleDriverEdit(
                                          driver.id,
                                          parseFloat(event.target.value) || 0,
                                        )
                                      }
                                      className={`w-full px-2 py-1 text-right bg-white border rounded text-[#18365E] font-semibold transition-all ${
                                        changed
                                          ? 'border-[#FF6E4C] bg-[#FF6E4C]/5 ring-1 ring-[#FF6E4C]/20'
                                          : 'border-[#e4e4e7] hover:border-[#3b82f6] focus:border-[#3b82f6] focus:ring-1 focus:ring-[#3b82f6]/20'
                                      } focus:outline-none`}
                                    />
                                  </td>
                                  <td className="px-2 py-2.5 text-center">
                                    <Badge
                                      className={`text-[9px] px-1 py-0 ${
                                        driver.confidence === 'High'
                                          ? 'bg-[#18365E]/10 text-[#18365E]'
                                          : driver.confidence === 'Medium'
                                            ? 'bg-[#f59e0b]/20 text-[#f59e0b]'
                                            : 'bg-[#ef4444]/20 text-[#ef4444]'
                                      }`}
                                    >
                                      {driver.confidence}
                                    </Badge>
                                  </td>
                                  <td className="px-2 py-2.5">
                                    <div className="flex items-center gap-1">
                                      {getSourceIcon(driver.sourceType)}
                                      <span className="text-[9px] text-[#52525b] font-medium truncate max-w-[80px]">
                                        {driver.source}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2.5">
                                    <div
                                      className="flex items-center gap-1"
                                      title="Share of the model's total sensitivity span"
                                    >
                                      <div className="flex-1 h-1.5 bg-[#e4e4e7] rounded-full overflow-hidden">
                                        <div
                                          className={`h-full transition-all ${
                                            impact >= 80
                                              ? 'bg-[#18365E]'
                                              : impact >= 40
                                                ? 'bg-[#f59e0b]'
                                                : 'bg-[#71717a]'
                                          }`}
                                          style={{ width: `${impact}%` }}
                                        />
                                      </div>
                                      <span className="text-[9px] text-[#71717a] w-5 text-right font-medium">
                                        {impact}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2.5 text-center">
                                    {changed && (
                                      <button
                                        onClick={() => handleResetDriver(driver.id)}
                                        aria-label={`Reset ${driver.name}`}
                                        className="text-[#71717a] hover:text-[#FF6E4C] transition-colors"
                                      >
                                        <RefreshCw className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Financial Breakdown */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs font-bold text-[#18365E]">
                    Financial Breakdown (Year 1 Impact)
                  </h3>
                  <HelpCircle className="w-3.5 h-3.5 text-[#71717a]" />
                </div>
                <p className="text-[9px] text-[#52525b] font-medium">
                  {SCENARIO_META[activeScenario].name} · benefits ramp-adjusted
                </p>
              </div>
              <WaterfallChart scenario={selected} />
              <p className="text-[9px] text-[#717182] mt-3 pt-2.5 border-t border-[#e4e4e7]">
                Year-1 benefits are {Math.round(selected.year1RampFraction * 100)}% of the{' '}
                {formatUsd(selected.annualValueMid)} run-rate — adoption reaches full rate in{' '}
                {selected.rampMonths} months. Costs are {formatUsd(selected.implementationCost)}{' '}
                implementation plus {formatUsd(selected.operatingCostPerYear)} operating.
              </p>
            </div>
          </div>

          {/* RIGHT: Sensitivity + AI Insight */}
          <div className="space-y-6">
            {/* Sensitivity Analysis */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg p-4" data-testid="roi-sensitivity">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs font-bold text-[#18365E]">Sensitivity Analysis</h3>
                  <HelpCircle className="w-3.5 h-3.5 text-[#71717a]" />
                </div>
              </div>
              <p className="text-[9px] text-[#52525b] mb-3 font-medium">
                Each input swung across its own band · change in net 3-year value · ranked by
                magnitude
              </p>
              <div className="space-y-3">
                {model.sensitivity.map((row) => {
                  const isPrimary = row.rank <= 2;
                  const isSecondary = row.rank >= 3 && row.rank <= 5;
                  const downWidth = (Math.abs(row.downsideDelta) / maxSensitivityMagnitude) * 100;
                  const upWidth = (row.upsideDelta / maxSensitivityMagnitude) * 100;
                  return (
                    <div key={row.driverId}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {isPrimary && (
                            <Badge className="text-[10px] px-2 py-0.5 bg-[#FF6E4C] text-white font-bold">
                              #{row.rank}
                            </Badge>
                          )}
                          {isSecondary && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-[#18365E] text-white font-semibold">
                              #{row.rank}
                            </Badge>
                          )}
                          {row.rank > 5 && (
                            <Badge className="text-[8px] px-1 py-0 bg-slate-400/40 text-slate-600">
                              #{row.rank}
                            </Badge>
                          )}
                          <span
                            className={`text-[10px] text-[#27272a] truncate ${
                              isPrimary ? 'font-bold' : 'font-medium'
                            }`}
                            title={row.explanation}
                          >
                            {row.name}
                          </span>
                        </div>
                        <span className="text-[9px] text-[#52525b] font-medium shrink-0 ml-1.5">
                          {row.lowValue}–{row.highValue}
                        </span>
                      </div>
                      {/* A real tornado: each half is scaled to the driver's own
                          magnitude. The previous bars sized the downside as
                          `50 − share × 50`, which rendered an offset, not a value. */}
                      <div
                        className={`${
                          isPrimary ? 'h-5' : 'h-4'
                        } bg-[#fafafa] rounded overflow-hidden relative border ${
                          isPrimary ? 'border-[#FF6E4C]/30' : 'border-[#e4e4e7]'
                        }`}
                      >
                        <div className="absolute inset-0 flex">
                          <div className="w-1/2 flex justify-end">
                            <div
                              className={`${isPrimary ? 'bg-[#dc2626]' : 'bg-[#ef4444]/70'} h-full`}
                              style={{ width: `${downWidth}%` }}
                            />
                          </div>
                          <div className="w-px bg-[#71717a]" />
                          <div className="w-1/2">
                            <div
                              className={`${isPrimary ? 'bg-[#18365E]' : 'bg-[#18365E]/70'} h-full`}
                              style={{ width: `${upWidth}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-0.5 text-[8px] font-semibold">
                        <span className="text-[#ef4444]">
                          {row.downsideDelta === 0 ? '—' : formatUsd(row.downsideDelta)}
                        </span>
                        <span className="text-[#18365E]">
                          {row.upsideDelta === 0 ? '—' : `+${formatUsd(row.upsideDelta)}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-[#e4e4e7]">
                <div className="flex items-center gap-3 text-[9px]">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-1.5 bg-[#ef4444]/80 rounded" />
                    <span className="text-[#52525b] font-medium">Downside</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-1.5 bg-[#18365E]/80 rounded" />
                    <span className="text-[#52525b] font-medium">Upside</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Insight Panel — names whatever the tornado actually ranked first. */}
            {insight && (
              <div className="relative p-[2px] rounded-lg bg-gradient-to-r from-[#18365E]/20 via-[#FF6E4C]/20 to-[#18365E]/20">
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-[#FF6E4C]" />
                    <div className="flex-1">
                      <h4 className="text-xs font-bold text-[#18365E] flex items-center gap-1">
                        Validation Priorities
                        <Badge className="text-[8px] px-1 py-0 bg-[#18365E]/10 text-[#18365E] border border-[#18365E]/30">
                          Model-derived
                        </Badge>
                      </h4>
                    </div>
                  </div>
                  <p className="text-[10px] text-[#27272a] mb-3 font-medium leading-relaxed">
                    {insight}
                  </p>
                  <button
                    onClick={() => setShowDisclosure(true)}
                    className="text-[10px] text-[#18365E] hover:text-[#FF6E4C] transition-colors flex items-center gap-0.5 font-semibold"
                  >
                    <ArrowUpRight className="w-3 h-3" />
                    How these are computed
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EAROIModelPage;
